/**
 * Cari ekstre (W-26 "Ekstre"). The contact card only shows what is still open;
 * a month-end reconciliation needs every document in date order with the balance
 * walking along beside it.
 *
 * Sign convention is the one v_contact_balances uses: + = the contact owes us.
 * A sale invoice and a payment we made raise that, a purchase invoice and a
 * receipt we took lower it. Only receipt/payment move a contact's position — an
 * expense or an income written on a contact is a cost or a revenue, not a claim
 * (migration 0014, section A) — so the closing balance here is the balance the
 * card prints, currency by currency.
 *
 * Invoice totals and payment amounts are used whole instead of what is left of
 * them: an allocation lowers the invoice's remainder and the payment's free part
 * by the same amount, so the two ways of counting land on the same balance. They
 * only drift apart when a payment closes an invoice in another currency, and
 * then only in the two currencies involved.
 */
import { D, type Dec, type DecimalInput } from './money';

/** An invoice the way listContactStatement returns it. */
export interface StatementInvoiceLike {
  id: string;
  no?: string | null;
  date: string;
  /** 'sale' | 'purchase' */
  type?: string | null;
  status?: string | null;
  currency?: string | null;
  total?: DecimalInput;
}

/** A money movement the way listContactStatement returns it. */
export interface StatementTransactionLike {
  id: string;
  date: string;
  type?: string | null;
  currency?: string | null;
  amount?: DecimalInput;
  description?: string | null;
}

export interface StatementEntry {
  id: string;
  kind: 'invoice' | 'transaction';
  /** invoice type or transaction type — what the row is called on screen */
  docType: string;
  date: string;
  no: string | null;
  description: string | null;
  currency: string;
  /** raises what the contact owes us (borç) */
  debit: Dec;
  /** lowers it (alacak) */
  credit: Dec;
  /** running balance after this row */
  balance: Dec;
}

export interface StatementCurrency {
  currency: string;
  /** what was carried in from before the range */
  opening: Dec;
  entries: StatementEntry[];
  debit: Dec;
  credit: Dec;
  closing: Dec;
}

/** Documents that never moved money: a draft was not posted, a cancelled one was taken back. */
const OPEN_STATUSES = (status: string | null | undefined) => status !== 'draft' && status !== 'cancelled';

/** Same day: the invoice comes before the payment that settles it. */
function compare(a: StatementEntry, b: StatementEntry): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  if (a.kind !== b.kind) return a.kind === 'invoice' ? -1 : 1;
  const an = a.no ?? '';
  const bn = b.no ?? '';
  if (an !== bn) return an < bn ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function rows(invoices: StatementInvoiceLike[], transactions: StatementTransactionLike[]): StatementEntry[] {
  const out: StatementEntry[] = [];
  for (const i of invoices) {
    if (!OPEN_STATUSES(i.status)) continue;
    const total = D(i.total ?? 0);
    const sale = i.type !== 'purchase';
    out.push({
      id: i.id,
      kind: 'invoice',
      docType: i.type ?? 'sale',
      date: i.date,
      no: i.no ?? null,
      description: null,
      currency: i.currency ?? '',
      debit: sale ? total : D(0),
      credit: sale ? D(0) : total,
      balance: D(0),
    });
  }
  for (const tx of transactions) {
    if (tx.type !== 'receipt' && tx.type !== 'payment') continue;
    const amount = D(tx.amount ?? 0);
    const receipt = tx.type === 'receipt';
    out.push({
      id: tx.id,
      kind: 'transaction',
      docType: tx.type,
      date: tx.date,
      no: null,
      description: tx.description ?? null,
      currency: tx.currency ?? '',
      debit: receipt ? D(0) : amount,
      credit: receipt ? amount : D(0),
      balance: D(0),
    });
  }
  return out.sort(compare);
}

/**
 * The statement, one block per currency. Everything before `from` is folded into
 * the opening balance instead of being dropped, so the running balance is still
 * the contact's real position; everything after `to` is left out altogether.
 * The blocks come back busiest first so the currency the contact actually trades
 * in opens by default.
 */
export function contactStatement(
  invoices: StatementInvoiceLike[],
  transactions: StatementTransactionLike[],
  range: { from?: string; to?: string } = {},
): StatementCurrency[] {
  const groups = new Map<string, StatementCurrency>();
  for (const row of rows(invoices, transactions)) {
    if (range.to && row.date > range.to) continue;
    const g = groups.get(row.currency) ?? { currency: row.currency, opening: D(0), entries: [], debit: D(0), credit: D(0), closing: D(0) };
    const delta = row.debit.minus(row.credit);
    if (range.from && row.date < range.from) {
      g.opening = g.opening.plus(delta);
    } else {
      g.debit = g.debit.plus(row.debit);
      g.credit = g.credit.plus(row.credit);
      g.entries.push(row);
    }
    groups.set(row.currency, g);
  }
  const out = [...groups.values()];
  for (const g of out) {
    let balance = g.opening;
    for (const e of g.entries) {
      balance = balance.plus(e.debit).minus(e.credit);
      e.balance = balance;
    }
    g.closing = balance;
  }
  return out.sort((a, b) => {
    const moved = b.debit.plus(b.credit).comparedTo(a.debit.plus(a.credit));
    return moved !== 0 ? moved : a.currency < b.currency ? -1 : a.currency > b.currency ? 1 : 0;
  });
}
